import React from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { Button, isAvailableMission, isLockedMission, Label } from '@atlas/ui';

import MISSIONS_TRANSLATION_KEYS from 'components/Smartico/translationsKeys';

const linkStopPropagation = e => e.stopPropagation();

const withStopPropagation = action => e => {
  e.preventDefault();
  e.stopPropagation();
  action();
};

export const renderMissionActionButton = (
  mission,
  {
    buttonClassName = 'smartico-mission__button',
    hasDetailsFallback = true,
    isProcessing,
    onClaimMissionReward,
    onOpenMissionDetails,
    onStartMission,
    size = 'small',
  },
) => {
  if (mission.is_requires_optin && !mission.is_opted_in) {
    return (
      <Button
        className={buttonClassName}
        disabled={isLockedMission(mission) || isProcessing}
        onClick={withStopPropagation(() => {
          onStartMission(mission.id);
        })}
        size={size}
      >
        <Label message="start_mission" />
      </Button>
    );
  }

  if (mission.is_completed && mission.requires_prize_claim) {
    if (!mission.prize_claimed_date_ts && mission.prize_claim_expiration_date > Date.now()) {
      return (
        <Button
          className={buttonClassName}
          disabled={isLockedMission(mission)}
          onClick={withStopPropagation(() => onClaimMissionReward(mission))}
          size={size}
        >
          <Label message="claim_reward" />
        </Button>
      );
    }
  }

  if (isAvailableMission(mission)) {
    if (mission.cta_action && mission.cta_text) {
      if (isLockedMission(mission)) {
        return (
          <Button className={buttonClassName} disabled size={size}>
            {mission.cta_text}
          </Button>
        );
      }

      const isFullUrl = mission.cta_action.startsWith('http');

      if (isFullUrl) {
        return (
          <a
            className="smartico-mission__button-link"
            href={mission.cta_action}
            onClick={linkStopPropagation}
            rel="noopener noreferrer"
          >
            <Button className={buttonClassName} size={size}>
              {mission.cta_text}
            </Button>
          </a>
        );
      } else {
        return (
          <Link
            className="smartico-mission__button-link"
            onClick={linkStopPropagation}
            to={mission.cta_action}
          >
            <Button className={buttonClassName} size={size}>
              {mission.cta_text}
            </Button>
          </Link>
        );
      }
    }
  }

  if (hasDetailsFallback) {
    return (
      <Button
        className={buttonClassName}
        onClick={withStopPropagation(() => onOpenMissionDetails(mission))}
        size={size}
      >
        <Label message="view_details" />
      </Button>
    );
  }

  return null;
};

export const SmarticoMissionsListTabs = ({ onSelectTab, selectedTab, tabs }) => {
  return (
    <div className="smartico-missions-tabs">
      {tabs.map(tab => (
        <div
          className={cx('tab-link smartico-missions-tabs__tab title-300-text', {
            active: tab.key === selectedTab,
          })}
          key={tab.key}
          onClick={onSelectTab(tab.key)}
        >
          <Label message={tab.translatesKey} />
        </div>
      ))}
    </div>
  );
};

export const SMARTICO_PAGE_TAB_KEYS = {
  AVAILABLE: 'available',
  COMPLETED: 'completed',
  LOCKED: 'locked',
  OVERVIEW: 'overview',
};

export const SMARTICO_PAGE_TABS = [
  {
    key: SMARTICO_PAGE_TAB_KEYS.OVERVIEW,
    translatesKey: MISSIONS_TRANSLATION_KEYS.MISSIONS_TAB_OVERVIEW,
  },
  {
    key: SMARTICO_PAGE_TAB_KEYS.AVAILABLE,
    translatesKey: MISSIONS_TRANSLATION_KEYS.MISSIONS_TAB_AVAILABLE,
  },
  {
    key: SMARTICO_PAGE_TAB_KEYS.COMPLETED,
    translatesKey: MISSIONS_TRANSLATION_KEYS.MISSIONS_TAB_COMPLETED,
  },
  {
    key: SMARTICO_PAGE_TAB_KEYS.LOCKED,
    translatesKey: MISSIONS_TRANSLATION_KEYS.MISSIONS_TAB_LOCKED,
  },
];

const compactPriceFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  notation: 'compact',
});

export const formatTaskReward = value =>
  Number.isFinite(value) ? compactPriceFormatter.format(value) : String(value);
